import type { StickyZone } from '@view-model/models/sticky/StickyZoneView';
import { StickyZoneCollection, StickyZoneRepository } from '@view-model/models/sticky/StickyZoneView';
import type { ModelCommentContents } from '@view-model/models/sticky/ModelComment/domain/ModelCommentContents';
import { ModelCommentCascadeRepository } from '@view-model/models/sticky/ModelComment';
import type { StickyNode } from '@view-model/models/sticky/StickyNodeView';
import { NodeCollection, NodeRepository } from '@view-model/models/sticky/StickyNodeView';
import type { LinkEntity } from '@view-model/models/sticky/StickyLink';
import { LinkCollection } from '@view-model/models/sticky/StickyLink/domain/LinkCollection';
import { LinkRepository } from '@view-model/models/sticky/StickyLink';
import type { StickyModel } from '@view-model/domain/model/StickyModel';
import { StickyModelContents } from '@view-model/domain/model/model-contents/StickyModelContents';
import type { DescriptionPanelCollectionContents } from '@view-model/models/sticky/DescriptionPanel/domain/DescriptionPanelCollectionContents';
import { DescriptionPanelCascadeRepository } from '@view-model/models/sticky/DescriptionPanel';
import type { FileContainerCollectionContents } from '@view-model/models/sticky/FileContainer/domain/FileContainerCollectionContents';
import { FileContainerCascadeRepository } from '@view-model/models/sticky/FileContainer/domain/FileContainerCascadeRepository';
import type { AnnotationLinkCollection } from '@view-model/models/sticky/AnnotationLink/domain/AnnotationLinkCollection';
import { AnnotationLinkRepository } from '@view-model/models/sticky/AnnotationLink/domain/AnnotationLinkRepository';
import { ElementDescriptionSet, ElementDescriptionSetRepository } from '@view-model/models/sticky/ElementDescription';
import type { IPositionSetRepository, PositionSet } from '@view-model/models/common/PositionSet';
import type { DisplayOrderTree } from '@model-framework/display-order';
import { DisplayOrderRepository } from '@model-framework/display-order/infrastructure';
import type { ViewModelId } from 'balus-common';
import { ElementDescriptionTarget } from '@view-model/models/sticky/ElementDescription/domain/ElementDescriptionTarget';

type StickyModelElementRepositories = {
  nodeRepository: NodeRepository;
  linkRepository: LinkRepository;
  zoneRepository: StickyZoneRepository;
  zonePositionsRepository: IPositionSetRepository;
  descriptionPanelsRepository: DescriptionPanelCascadeRepository;
  fileContainersRepository: FileContainerCascadeRepository;
  annotationLinksRepository: AnnotationLinkRepository;
  nodeDescriptionSetRepository: ElementDescriptionSetRepository;
  zoneDescriptionSetRepository: ElementDescriptionSetRepository;
  displayOrderRepository: DisplayOrderRepository;
};

export class StickyModelCascadeRepository {
  private readonly commentRepository: ModelCommentCascadeRepository;

  constructor(private readonly viewModelId: ViewModelId) {
    this.commentRepository = new ModelCommentCascadeRepository(this.viewModelId);
  }

  async saveContents(
    model: StickyModel,
    nodes: StickyNode[],
    links: LinkEntity[],
    zones: StickyZone[],
    zonePositions: PositionSet,
    commentContents: ModelCommentContents,
    descriptionPanelContents: DescriptionPanelCollectionContents,
    fileContainerContents: FileContainerCollectionContents,
    annotationLinks: AnnotationLinkCollection,
    nodeDescriptions: ElementDescriptionSet,
    zoneDescriptions: ElementDescriptionSet,
    displayOrderTree: DisplayOrderTree
  ): Promise<void> {
    const {
      nodeRepository,
      linkRepository,
      zoneRepository,
      zonePositionsRepository,
      descriptionPanelsRepository,
      fileContainersRepository,
      annotationLinksRepository,
      nodeDescriptionSetRepository,
      zoneDescriptionSetRepository,
      displayOrderRepository,
    } = this.createRepositories(model);

    await Promise.all([
      nodeRepository.saveNodes(nodes),
      linkRepository.saveLinks(links),
      zoneRepository.saveZones(zones),
      zonePositionsRepository.saveMany(zonePositions),
      this.commentRepository.saveContents(commentContents),
      descriptionPanelsRepository.saveContents(descriptionPanelContents),
      fileContainersRepository.saveContents(fileContainerContents),
      annotationLinksRepository.saveContents(annotationLinks),
      nodeDescriptionSetRepository.save(nodeDescriptions),
      zoneDescriptionSetRepository.save(zoneDescriptions),
      displayOrderRepository.save(displayOrderTree),
    ]);
  }

  async loadContents(model: StickyModel): Promise<StickyModelContents> {
    const {
      nodeRepository,
      linkRepository,
      zoneRepository,
      zonePositionsRepository,
      descriptionPanelsRepository,
      fileContainersRepository,
      annotationLinksRepository,
      nodeDescriptionSetRepository,
      zoneDescriptionSetRepository,
      displayOrderRepository,
    } = this.createRepositories(model);

    const [
      nodes,
      links,
      zones,
      comments,
      descriptionPanels,
      fileContainers,
      annotationLinks,
      nodeDescriptions,
      zoneDescriptions,
      displayOrderTree,
    ] = await Promise.all([
      nodeRepository.loadNodes(),
      linkRepository.loadLinks(),
      zoneRepository.loadZones(),
      this.commentRepository.loadContents(model.key),
      descriptionPanelsRepository.loadContents(),
      fileContainersRepository.loadContents(),
      annotationLinksRepository.loadContents(),
      nodeDescriptionSetRepository.get(),
      zoneDescriptionSetRepository.get(),
      displayOrderRepository.load(),
    ]);

    const zonePositions = await zonePositionsRepository.getMany(
      zones.map((zone: StickyZone) => {
        return zone.id;
      })
    );

    const { contents } = StickyModelContents.fromFragments(
      model.key,
      model.version,
      new NodeCollection(nodes),
      new LinkCollection(links),
      new StickyZoneCollection(zones),
      zonePositions,
      comments,
      descriptionPanels,
      fileContainers,
      annotationLinks,
      nodeDescriptions || ElementDescriptionSet.fromArray([]),
      zoneDescriptions || ElementDescriptionSet.fromArray([]),
      displayOrderTree
    );

    return contents;
  }

  private createRepositories(model: StickyModel): StickyModelElementRepositories {
    const nodeRepository = new NodeRepository(this.viewModelId, model.id);
    const linkRepository = new LinkRepository(this.viewModelId, model.id);
    const zoneRepository = new StickyZoneRepository(this.viewModelId, model.id);
    const zonePositionsRepository = StickyZoneRepository.createPositionsRepository(this.viewModelId, model.id);
    const displayOrderRepository = new DisplayOrderRepository(this.viewModelId, model.id);

    return {
      nodeRepository,
      linkRepository,
      zoneRepository,
      zonePositionsRepository,
      descriptionPanelsRepository: new DescriptionPanelCascadeRepository(this.viewModelId, model.id),
      fileContainersRepository: new FileContainerCascadeRepository(this.viewModelId, model.id),
      annotationLinksRepository: new AnnotationLinkRepository(this.viewModelId, model.id),
      nodeDescriptionSetRepository: ElementDescriptionSetRepository.buildFromIds(
        this.viewModelId,
        model.id,
        ElementDescriptionTarget.Node
      ),
      zoneDescriptionSetRepository: ElementDescriptionSetRepository.buildFromIds(
        this.viewModelId,
        model.id,
        ElementDescriptionTarget.Zone
      ),
      displayOrderRepository,
    };
  }
}
